import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import { useScrollLock } from '../hooks/useScrollLock';

/**
 * Returns the canonical (left) page number for spread-aware navigation.
 * - Page 1: front cover (solo) → 1
 * - Last page: back cover (solo) → totalPages
 * - Even pages: left of spread → same page
 * - Odd pages (>1, <totalPages): right of spread → pageNumber - 1
 */
const getCanonicalPageNum = (pageNumber, totalPages) => {
  if (pageNumber === 1 || pageNumber === totalPages) return pageNumber;
  return pageNumber % 2 === 0 ? pageNumber : pageNumber - 1;
};

// Flatten menu tree recursively into [{name, mappedPage}]
const flattenMenuItems = (nodes) => {
  const result = [];
  const traverse = (list) => {
    for (const node of list) {
      if (node.mappedPage !== null && node.mappedPage !== undefined) {
        result.push({ name: node.name, mappedPage: node.mappedPage });
      }
      if (node.children?.length) traverse(node.children);
    }
  };
  traverse(nodes);
  return result;
};

const CatalogSearchDrawer = ({ pages = [], menuTree = [], currentPage, onNavigate, isOpen, onClose, embedMode = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const totalPages = pages.length;
  useScrollLock(isOpen);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const normalizedQuery = query.trim().toLowerCase();
  const menuItems = flattenMenuItems(menuTree);

  // Search: match menu item names + keywords, deduplicate by canonical page
  const results = normalizedQuery.length === 0
    ? []
    : (() => {
        const seen = new Set();
        const combined = [];

        // 1. Menu item name matches (show first)
        for (const item of menuItems) {
          if (item.name.toLowerCase().includes(normalizedQuery)) {
            const canonical = getCanonicalPageNum(item.mappedPage, totalPages);
            if (!seen.has(canonical)) {
              seen.add(canonical);
              const page = pages.find((p) => p.pageNumber === canonical)
                || pages.find((p) => p.pageNumber === item.mappedPage);
              if (page) {
                combined.push({ ...page, _navigateTo: canonical, _menuLabel: item.name });
              }
            }
          }
        }

        // 2. Keyword matches
        pages
          .filter((page) => {
            const keywords = page.keywords || [];
            return keywords.some((kw) => kw.toLowerCase().includes(normalizedQuery));
          })
          .sort((a, b) => (a.pageNumber || 0) - (b.pageNumber || 0))
          .forEach((page) => {
            const canonical = getCanonicalPageNum(page.pageNumber, totalPages);
            if (!seen.has(canonical)) {
              seen.add(canonical);
              const canonicalPage = pages.find((p) => p.pageNumber === canonical) || page;
              combined.push({ ...canonicalPage, _navigateTo: canonical });
            }
          });

        return combined.sort((a, b) => (a._navigateTo || 0) - (b._navigateTo || 0));
      })();

  const handleNavigate = (navigateTo) => {
    onNavigate(navigateTo);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={`fixed inset-0 bg-black/40 ${embedMode ? 'z-40' : 'z-40'}`}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full bg-card border-r border-border shadow-elevation-xl
          flex flex-col transition-transform duration-250 ease-in-out
          ${embedMode ? 'z-50 w-64' : 'z-50 w-80'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Icon name="Search" size={18} className="text-foreground" />
            <span className="text-sm font-semibold text-foreground">{t('catalogSearch.title')}</span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Search input */}
        <div className="px-3 py-3 border-b border-border flex-shrink-0">
          <div className="relative">
            <Icon name="Search" size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('catalogSearch.placeholder')}
              className="w-full text-sm bg-background border border-border rounded-md pl-8 pr-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {normalizedQuery.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 pb-8">
              <Icon name="Search" size={32} className="text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">{t('catalogSearch.empty')}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 pb-8">
              <Icon name="SearchX" size={32} className="text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">{t('catalogSearch.noResults')}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">"{query}"</p>
            </div>
          ) : (
            <div className="p-3">
              <p className="text-xs text-muted-foreground mb-3">
                {t('catalogSearch.resultsCount', { count: results.length })}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {results.map((page) => {
                  const nav = page._navigateTo ?? page.pageNumber;
                  const isActive = nav === currentPage || nav === currentPage + 1;
                  // Build spread badge label
                  let spreadLabel;
                  if (page.pageNumber === 1) {
                    spreadLabel = t('pageKeywords.frontCover');
                  } else if (page.pageNumber === totalPages) {
                    spreadLabel = t('pageKeywords.backCover');
                  } else {
                    const left = nav;
                    const right = left + 1;
                    spreadLabel = right <= totalPages
                      ? t('pageKeywords.spreadLabel', { left, right })
                      : t('catalogSearch.page', { number: left });
                  }
                  const isMenuResult = Boolean(page._menuLabel);
                  return (
                    <button
                      key={`${page.id}-${nav}`}
                      onClick={() => handleNavigate(nav)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all hover:scale-105 hover:shadow-md
                        ${isActive ? 'border-accent' : 'border-border hover:border-accent/50'}`}
                    >
                      {/* Menu label strip */}
                      {isMenuResult && (
                        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1 px-1.5 py-0.5 bg-accent/90 text-accent-foreground">
                          <Icon name="Menu" size={10} className="flex-shrink-0" />
                          <span className="text-xs font-medium leading-tight truncate">{page._menuLabel}</span>
                        </div>
                      )}
                      {/* Thumbnail */}
                      {page.thumbnailUrl || page.imageUrl ? (
                        <img
                          src={page.thumbnailUrl || page.imageUrl}
                          alt={page._menuLabel || spreadLabel}
                          className="w-full aspect-[3/4] object-cover bg-muted"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full aspect-[3/4] bg-muted flex items-center justify-center">
                          <Icon name="FileText" size={24} className="text-muted-foreground" />
                        </div>
                      )}
                      {/* Spread label badge */}
                      <div className={`absolute bottom-0 left-0 right-0 px-1.5 py-1 text-center text-xs font-medium leading-tight
                        ${isActive ? 'bg-accent text-accent-foreground' : 'bg-background/90 text-foreground'}`}>
                        {spreadLabel}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CatalogSearchDrawer;
